import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { MusicNotesSimple, GenderFemale, GenderMale, WaveSine } from "@phosphor-icons/react";
import { API } from "@/lib/api";
import AudioPlayer from "@/components/AudioPlayer";
import { useLang } from "@/context/LanguageContext";
import axios from "axios";

const ACCENTS = ["hsl(351 100% 78%)", "hsl(200 75% 72%)", "hsl(298 55% 74%)", "hsl(45 100% 70%)"];

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el && window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
};

export default function ShowcaseGallery() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const currentAudio = useRef(null);

  useEffect(() => {
    axios
      .get(`${API}/showcase`)
      .then((res) => setItems(res.data))
      .catch(() => setItems([]))
      .finally(() => setLoaded(true));
  }, []);

  // Pausar cualquier otro audio al reproducir uno nuevo
  const handlePlay = (audioEl) => {
    if (currentAudio.current && currentAudio.current !== audioEl) {
      currentAudio.current.pause();
    }
    currentAudio.current = audioEl;
  };

  if (!loaded || items.length === 0) return null;

  return (
    <section id="ejemplos" className="relative py-24 max-w-7xl mx-auto px-5 sm:px-8">
      <div className="text-center max-w-2xl mx-auto mb-16">
        <p className="font-hand text-3xl text-primary mb-2">{t("showcase.kicker")}</p>
        <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight text-foreground">
          {t("showcase.title")}
        </h2>
        <p className="mt-4 text-foreground/60 font-medium">
          {t("showcase.subtitle")}
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((it, i) => {
          const accent = ACCENTS[i % ACCENTS.length];
          const VoiceIcon = it.voice === "masculina" ? GenderMale : GenderFemale;
          return (
            <motion.div
              key={it.id}
              data-testid={`showcase-card-${it.id}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ type: "spring", stiffness: 140, damping: 18, delay: (i % 3) * 0.08 }}
              whileHover={{ y: -5 }}
              className="rounded-3xl bg-white p-5 shadow-glass border border-white"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: accent }}>
                  <MusicNotesSimple weight="duotone" size={22} className="text-white" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-display font-semibold text-base text-foreground leading-tight truncate">{it.title}</h3>
                  {it.occasion && <p className="text-xs font-bold text-foreground/40 truncate">{it.occasion}</p>}
                </div>
              </div>

              <div className="flex flex-wrap gap-1.5 mb-4">
                {it.genre && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-[11px] font-bold text-foreground/60">
                    <WaveSine weight="duotone" size={12} /> {it.genre}
                  </span>
                )}
                {it.voice && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-[11px] font-bold text-foreground/60 capitalize">
                    <VoiceIcon weight="duotone" size={12} /> {it.voice}
                  </span>
                )}
                <span className="inline-flex items-center rounded-full bg-primary/10 px-2.5 py-1 text-[11px] font-bold text-primary">
                  {t("showcase.preview")}
                </span>
              </div>

              <AudioPlayer
                testid={`showcase-player-${it.id}`}
                src={`${API}/showcase/${it.id}/audio`}
                accent={accent}
                onPlay={handlePlay}
                maxDuration={30}
              />
            </motion.div>
          );
        })}
      </div>

      <div className="text-center mt-12">
        <motion.button
          data-testid="showcase-cta"
          whileHover={{ scale: 1.05, rotate: -1 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => scrollTo("crear")}
          className="rounded-full bg-primary px-8 py-4 font-bold text-primary-foreground shadow-soft-lg"
        >
          {t("showcase.cta")}
        </motion.button>
      </div>
    </section>
  );
}
